/**
 * Courses — listing and create.
 *
 * Feature parity with Studio's course tab, which is more than it looks:
 *   active / archived split          Studio keeps ended courses in a second tab
 *   in-process course actions        create and rerun are ASYNC; Studio shows pending
 *                                    and failed rows with a dismiss action
 *   search + org filter             applied here, over one tenant-scoped read
 *   Studio outline + live links     per course
 *
 * The in-process rows are the part most rebuilds miss. `POST /course/` returns before
 * the course exists, so without them a new course simply does not appear and the author
 * cannot tell whether it worked.
 *
 * WHERE THE ROWS COME FROM, AND WHY THAT CHANGED
 * ----------------------------------------------
 * This was the last admin screen still building its list out of Studio's
 * `/api/contentstore/v2/home/courses`, and it was WRONG BY CONSTRUCTION rather than merely
 * slow. That endpoint ignores its own `org` parameter, so the page compensated by asking once
 * per org the tenant owns and filtering in the browser. A LICENSED course belongs to an org
 * the tenant does NOT own, so it was never asked for and could never appear:
 *
 *     surface                          courses shown
 *     learner catalogue                5
 *     admin dashboard count + recent   5
 *     /api/pathways/v1/academy/courses 5
 *     /admin/courses                   4      <- this page, one licensed course missing
 *
 * The rows now come from `academy/courses`, the same tenant-scoped endpoint every other
 * admin screen uses, so all of them agree by sharing one source rather than by coincidence.
 * It is also ONE request instead of one per org: on a two-org tenant the old code fired two
 * byte-identical 63.6 KB requests, the last duplicate requests left in the app.
 *
 * WHAT STUDIO IS STILL NEEDED FOR — and it is not the courses. Two things, and BOTH arrive in
 * the single 898-byte `/api/contentstore/v1/home` response this page already fetched:
 * `allowed_organizations` (which orgs the author may create in) and
 * `in_process_course_actions` (the pending and failed create/rerun rows). The Studio-only
 * COURSE fields — `url`, `lms_link`, `rerun_link` — are not needed and never were: the first
 * two are documented below as broken on a headless install and were already unused, and there
 * is no rerun control on this page to feed the third. So nothing regresses by dropping them,
 * and no per-course Studio call is reintroduced to fetch them.
 *
 * OWNED VS LICENSED. A tenant's catalogue contains courses from orgs it does not own, so "in
 * my catalogue" and "I may edit this" are different questions and the page answers both:
 * every row says which it is, the filter selects between them, and a licensed row offers no
 * management action. The refusal is enforced on the SERVER — see
 * `openedx_thinlms_authoring/ownership.py`, which exists because Studio's own write check
 * short-circuits to True for these accounts. Hiding the buttons is the courtesy, not the rule.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  Field,
  Icon,
  SearchField,
  SelectField,
  Spinner,
  Tabs,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { getConfig } from '../../shared/lib/config';
import { ApiError, openedx } from '../../shared/lib/openedx';
import {
  courseKeyFor,
  createCourse,
  defaultRun,
  deriveNumber,
  resolveOrgs,
  studioHome,
  type InProcessCourse,
} from '../../shared/lib/studio';
import type { AcademyCourse } from '../../shared/lib/academy';
import { coursePriceView, seamHasPrices, type PricingSeam } from '../../shared/lib/pricing';
import { useAdminCoursePrices } from '../../shared/lib/usePricing';
import { PriceTag } from '../../shared/ui/price';
import { useAcademyCourses } from '../useAcademyCourses';
import { PageHeader } from '../AdminShell';

/**
 * Where "edit this course" goes.
 *
 * NOT Studio. Verawood deleted the server-rendered outline template, so `course.url`
 * (`/course/<key>`) now 302s to a micro-frontend URL that does not exist on a headless
 * install — a link that looks fine and dead-ends. Our own outline is the real page.
 */
/** v2 paginates; 24 fits the table without the page becoming a scroll marathon. */
const PAGE_SIZE = 24;

const courseHref = (course: AcademyCourse) =>
  `/admin/courses/${encodeURIComponent(course.course_key)}`;

/**
 * Where "view as a learner" goes.
 *
 * OURS, not `course.lms_link`, for the same reason `course.url` is not used above. Studio builds
 * that field as the platform's own jump-to-courseware URL — measured:
 *
 *     http://<tenant>/courses/<course_key>/jump_to/block-v1:…+type@course+block@course
 *
 * The host is right (it is built from the tenant's LMS_ROOT_URL, which is this app's origin) and
 * the PATH is not: `/courses/…/jump_to/…` is a platform route this app does not serve, and
 * `/courses/` is one of our own SPA routes, so the request never reaches the LMS and the router
 * has nothing to match. The result is a button that looks right and lands nowhere.
 *
 * Our player takes the course key and resolves where to start by itself, which is what the
 * jump-to URL was doing the long way round.
 */
const learnerHref = (course: AcademyCourse) =>
  `/learn/${encodeURIComponent(course.course_key)}`;

type TabId = 'active' | 'archived';

/** All / Owned / Licensed. `''` is "all", so it is also the empty value of the `<select>`. */
type Ownership = '' | 'owned' | 'licensed';

/**
 * Whether a course has ended, which is Studio's ONLY definition of "archived".
 *
 * Copied from the platform rather than invented, so the two tabs cannot drift from what
 * Studio itself shows. `CourseOverview.get_courses_by_status` filters
 * `end IS NULL OR end >= now` for active and `end < now` for archived — this is that
 * predicate, and the reason the split can happen here at all: `end` is on every row, so ONE
 * request serves both tabs and both tab counts. Asking the server twice, once per status,
 * would be two requests to partition a list already in hand.
 */
const isArchived = (course: AcademyCourse) =>
  course.end !== null && new Date(course.end).getTime() < Date.now();

export default function AdminCourses() {
  const config = getConfig();
  const [tab, setTab] = useState<TabId>('active');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [org, setOrg] = useState('');
  const [creating, setCreating] = useState(false);

  const [ownership, setOwnership] = useState<Ownership>('');

  const home = useQuery({ queryKey: ['studio-home'], queryFn: studioHome, staleTime: 5 * 60_000 });

  /**
   * THE ONE REQUEST THIS PAGE MAKES FOR ITS ROWS.
   *
   * The shared hook, deliberately: it is the same query key the enrolments, grades, progress
   * and certificates screens use, so this page agrees with them by reading the same cache
   * entry rather than by fetching the same thing again. Moving between admin screens now
   * costs no course request at all.
   *
   * The tenant boundary is the SERVER'S — `writable_orgs` plus this tenant's licences — so
   * there is no org to send and nothing to re-filter here. That is the whole difference from
   * the per-org fan-out this replaces, where the boundary lived in the browser.
   */
  const listing = useAcademyCourses();

  /** Both tabs, partitioned from the one list. `count` on each tab comes free. */
  const byStatus = useMemo(() => {
    const all = [...listing.all].sort((a, b) => a.display_name.localeCompare(b.display_name));
    return {
      active: all.filter((course) => !isArchived(course)),
      archived: all.filter(isArchived),
    };
  }, [listing.all]);

  const inTab = byStatus[tab];

  /**
   * How many of the CURRENT TAB are owned and how many licensed.
   *
   * Counted per tab rather than over the whole catalogue because the filter acts on the tab,
   * and a control offering "Licensed (1)" that then empties the table would be counting
   * something the user cannot see.
   */
  const ownedCount = useMemo(() => inTab.filter((course) => course.owned).length, [inTab]);
  const licensedCount = inTab.length - ownedCount;

  /**
   * A CONTROL THAT CANNOT CHANGE THE RESULT IS A LABEL, NOT A CONTROL.
   *
   * The same rule the learner's chips follow, and for the same reason: an academy with no
   * licences would get an "Owned / Licensed" filter whose only working option is the one
   * already applied, and whose other option is a guaranteed empty table. That teaches the
   * reader the page is broken. So the filter appears only when the tab genuinely holds both
   * kinds — which for most academies means it is simply not there.
   */
  const showOwnership = ownedCount > 0 && licensedCount > 0;
  const activeOwnership: Ownership = showOwnership ? ownership : '';

  /** The rows for the chosen tab, after ownership, the org filter and the search box. */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return inTab.filter(
      (course) =>
        (activeOwnership === '' ||
          (activeOwnership === 'owned' ? course.owned : !course.owned)) &&
        (org ? course.org === org : true) &&
        (term
          ? [course.display_name, course.number, course.run, course.org].some((field) =>
              (field ?? '').toLowerCase().includes(term),
            )
          : true),
    );
  }, [inTab, activeOwnership, org, search]);

  const shown = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  const creatableOrgs = resolveOrgs(home.data?.allowed_organizations ?? [], config.tenant.orgs);
  const tenantOrgs = config.tenant.orgs;

  const rows = shown;

  /**
   * PRICES FOR THIS PAGE OF ROWS, AND ONLY THIS PAGE (spec R6.2).
   *
   * 24 keys, not the academy's catalogue. The rows come from one 200-course read because
   * `has_learners` and the ownership filter need the whole list in hand; a PRICE is needed
   * only where one is rendered, which is the 24 cells on screen. At 1000 courses those are
   * a ~1 KB request and a ~40 KB one, and the larger one would be re-requested every time
   * the administrator changed tab.
   *
   * The seam is asked DIRECTLY rather than through `useAcademyCourses`'s own `priceFor`,
   * because this page's keys are a function of the list it is still waiting for: the visible
   * rows exist only after the fetch, the tab, the ownership filter, the org filter, the search
   * box and the pager have all been applied. `priceFor` is for a caller that knows its keys up
   * front — see `AdminPaths`'s pricing panel, which does. Both go through the same cache key,
   * so the two screens still agree by sharing one entry rather than by coincidence.
   */
  const visibleKeys = useMemo(() => shown.map((course) => course.course_key), [shown]);
  const prices = useAdminCoursePrices(visibleKeys);

  /**
   * A COLUMN WHOSE EVERY CELL READS "FREE" IS FURNITURE.
   *
   * The same rule this file already states for the ownership filter — "A CONTROL THAT CANNOT
   * CHANGE THE RESULT IS A LABEL, NOT A CONTROL" — applied to a column. An academy that prices
   * nothing gets no price column, which is every academy until `paid_enrollment` is switched on
   * for one. `seamHasPrices` answers false for a FAILED document as well as an empty one, so a
   * pricing outage cannot conjure a column onto a page that has never had one; the cells that
   * do exist still resolve `unknown` and say so.
   */
  const showPrices = seamHasPrices(prices);

  /**
   * Pending and failed creates, from the home context this page already fetched.
   *
   * Studio's, and it has to be: whether a course is mid-creation is a Studio job state and
   * there is no row in the academy catalogue until it finishes. What changed is that it no
   * longer costs a course listing per org to find out — see the module docstring.
   */
  const pending = home.data?.in_process_course_actions ?? [];
  // Paged over what the filters left, not over what the server returned: the fetch is the whole
  // catalogue now, so the pager has to count the rows actually on offer or it offers empty pages.
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const columns: Column<AcademyCourse>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Course',
        render: (course) => (
          <>
            <a href={courseHref(course)}>{course.display_name}</a>
            <div className="admin__sub">{course.course_key}</div>
          </>
        ),
      },
      {
        key: 'org',
        header: 'Organisation',
        render: (course) => (
          <>
            <span className="mono">{course.org}</span>{' '}
            {/*
              THE SERVER'S ANSWER, NOT A GUESS FROM THE ORG LIST.
              `owned` is computed by `read_courses` from the academy's own orgs, which is the
              same set that authorises management. Re-deriving it here from `config.tenant.orgs`
              would be a second opinion free to disagree with the one the server enforces.
            */}
            {course.owned ? (
              <Badge tone="info">Owned by this academy</Badge>
            ) : (
              <Badge tone="warning">Licensed from {course.org}</Badge>
            )}
          </>
        ),
      },
      /*
       * PRICE (spec R6.2), after the organisation because ownership is what decides whether the
       * number in this cell is one this academy may change.
       *
       * Read through `coursePriceView`, which is the only thing that knows the difference
       * between a course ABSENT from a price document that loaded — free, the tenant default —
       * and a document that did not load, which is `unknown` and renders "Price unavailable".
       * A cell that resolved that itself would be the fourth place in the app deciding what a
       * missing price means, and the one that got it wrong is `CourseHome.tsx:836`.
       *
       * NO EDIT CONTROL HERE, for either kind of row. A price is set on the course's own
       * settings page, where the author can see it next to the schedule that decides whether
       * anybody can buy it; a second entry point in a table cell would be a second form for one
       * field. A LICENSED row shows the price it is being sold at in this academy and offers
       * nothing to press, which is the treatment the Actions column already gives it — and
       * until Open Question 4 is answered nothing writes a price row for a licensed course, so
       * in practice that cell reads "Free".
       */
      ...(showPrices
        ? [
            {
              key: 'price',
              header: 'Price',
              numeric: true,
              render: (course: AcademyCourse) => (
                <PriceCell course={course} prices={prices} />
              ),
            } satisfies Column<AcademyCourse>,
          ]
        : []),
      { key: 'run', header: 'Run', render: (course) => course.run, mono: true },
      {
        key: 'actions',
        header: 'Actions',
        render: (course) =>
          course.owned ? (
            <div className="admin__row-actions">
              <Link className="ui-btn" data-variant="ghost" to={courseHref(course)}>
                Edit content
              </Link>
              <Link className="ui-btn" data-variant="ghost" to={`${courseHref(course)}/settings`}>
                Settings
              </Link>
              <Link className="ui-btn" data-variant="ghost" to={learnerHref(course)}>
                View as learner
              </Link>
            </div>
          ) : (
            /*
             * A LICENSED COURSE IS READ-ONLY, AND THIS IS THE COURTESY HALF OF THAT.
             *
             * No "Edit content" and no "Settings", because the licence grants the right to
             * SHOW this course and not to change it — its owner is another academy, and an
             * edit here would alter their content on their own host too.
             *
             * The rule itself is enforced in the CMS, not by this branch: see
             * `openedx_thinlms_authoring/ownership.py`. Offering no button is how an admin
             * finds out without hitting a 403; the 403 is what makes it true.
             */
            <div className="admin__row-actions">
              <Link className="ui-btn" data-variant="ghost" to={learnerHref(course)}>
                View as learner
              </Link>
              <Chip icon="lock">Licensed — only {course.org} can edit it</Chip>
            </div>
          ),
      },
    ],
    // `showPrices` and `prices` are in here because the price column CLOSES OVER THEM. With
    // the empty dependency list this memo used to carry, the column would be built once from
    // whatever the seam held on first render — `undefined` — and every cell would read "Price
    // unavailable" for the life of the page, refetch or not.
    [showPrices, prices],
  );

  return (
    <>
      <PageHeader
        title="Courses"
        /*
         * Says "and licensed" only when some are, because a tenant with no licences reading
         * "plus the ones licensed to it" would be told about a mechanism it has no instance
         * of — the same reason the ownership filter hides itself in that case.
         */
        description={
          licensedCount > 0
            ? `Courses in ${tenantOrgs.join(', ') || 'this academy'}, plus the ones licensed to it.`
            : `Courses in ${tenantOrgs.join(', ') || 'this academy'}.`
        }
        actions={
          creatableOrgs.length > 0 && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="add" />
              New course
            </Button>
          )
        }
      />

      {/* Async create/rerun. Without this row a new course looks like it failed. */}
      {pending.length > 0 && (
        <Banner tone={pending.some((p) => p.is_failed) ? 'danger' : 'info'}>
          {pending.map((item) => (
            <div key={`${item.org}+${item.number}+${item.run}`}>
              <strong>{item.display_name}</strong>{' '}
              {item.is_failed ? 'could not be created.' : 'is being created…'}{' '}
              {item.is_failed && <DismissFailed item={item} onDone={() => home.refetch()} />}
            </div>
          ))}
        </Banner>
      )}

      {creating && (
        <NewCourseCard
          slug={config.tenant.slug}
          orgs={creatableOrgs}
          onDone={() => setCreating(false)}
        />
      )}

      <Toolbar>
        <Tabs
          label="Course status"
          active={tab}
          onChange={(id) => {
            setTab(id as TabId);
            setPage(1);
          }}
          tabs={[
            { id: 'active', label: 'Active', count: byStatus.active.length },
            { id: 'archived', label: 'Archived', count: byStatus.archived.length },
          ]}
        />
        <div className="toolbar__end">
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Search courses…"
          />
          {/*
            A `<select>` and not a new segmented control, because that is what this toolbar
            already uses for "narrow this list by a property of the row" — the organisation
            filter directly below it. Same element, same class, same aria-label shape, and it
            appears under the same kind of condition: that one shows up only for a tenant with
            more than one org, this one only when both kinds of row are present.
          */}
          {showOwnership && (
            <select
              className="admin__select"
              value={ownership}
              onChange={(e) => {
                setOwnership(e.target.value as Ownership);
                setPage(1);
              }}
              aria-label="Filter by owned or licensed"
            >
              <option value="">All courses ({inTab.length})</option>
              <option value="owned">Owned by this academy ({ownedCount})</option>
              <option value="licensed">Licensed from another academy ({licensedCount})</option>
            </select>
          )}
          {tenantOrgs.length > 1 && (
            <select
              className="admin__select"
              value={org}
              onChange={(e) => {
                setOrg(e.target.value);
                setPage(1);
              }}
              aria-label="Filter by organisation"
            >
              <option value="">All organisations</option>
              {tenantOrgs.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          )}
        </div>
      </Toolbar>

      {listing.isPending ? (
        <Spinner label="Loading courses…" />
      ) : listing.isError ? (
        <Banner tone="danger">
          <strong>Could not load courses.</strong> The academy course list did not respond.
          Nothing has changed — retry, and check that the LMS is running.
        </Banner>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(course) => course.course_key}
            empty={
              // Named per filter, because "No archived courses" under a Licensed filter would
              // be describing the wrong thing — the tab has rows, this selection does not.
              activeOwnership === 'licensed'
                ? 'No licensed courses in this tab.'
                : activeOwnership === 'owned'
                  ? 'No courses of your own in this tab.'
                  : tab === 'active'
                    ? 'No active courses yet. Create one to get started.'
                    : 'No archived courses.'
            }
          />
          {pages > 1 && (
            <div className="admin__pager">
              <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                <Icon name="chevron_left" />
                Previous
              </Button>
              <span>
                Page {page} of {pages} · {filtered.length} course{filtered.length === 1 ? '' : 's'}
              </span>
              <Button variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                Next
                <Icon name="chevron_right" />
              </Button>
            </div>
          )}
        </Card>
      )}
    </>
  );
}

/** Map the server's status codes onto something an author can act on. */
function CreateError({ error }: { error: unknown }) {
  const status = error instanceof ApiError ? error.status : 0;
  const detail =
    error instanceof ApiError && typeof (error.body as { detail?: string })?.detail === 'string'
      ? (error.body as { detail: string }).detail
      : null;

  if (status === 409) {
    return (
      <Banner tone="danger">
        <strong>{detail ?? 'That course ID already exists.'}</strong> Change the number or
        run under &ldquo;Change&rdquo;.
      </Banner>
    );
  }
  if (status === 403) {
    return (
      <Banner tone="danger">
        <strong>{detail ?? 'You cannot create courses in this organisation.'}</strong> Ask
        an administrator for content-creator access.
      </Banner>
    );
  }
  return (
    <Banner tone="danger">
      <strong>The course was not created.</strong> {detail ?? 'Studio rejected the request.'}{' '}
      Nothing was created, so it is safe to try again.
    </Banner>
  );
}

/**
 * Create form.
 *
 * One required field. Organisation comes from permissions, number from the title, run
 * from the year — and the resulting key is shown as it is built, because an author will
 * meet that key later in URLs, reports and support tickets.
 */
function NewCourseCard({
  slug,
  orgs,
  onDone,
}: {
  slug: string;
  orgs: string[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [org, setOrg] = useState(orgs[0] ?? '');
  const [advanced, setAdvanced] = useState(false);
  const [number, setNumber] = useState('');
  const [run, setRun] = useState(defaultRun(new Date()));

  const effectiveNumber = number || deriveNumber(title);
  const key = courseKeyFor(org, effectiveNumber, run);

  const create = useMutation({
    // No `org` in the body — the server takes it from the tenant.
    mutationFn: () => createCourse(slug, { title: title.trim(), number: effectiveNumber, run }),
    onSuccess: (result) => {
      // The shared academy list, which is now where the rows come from — and `studio-home`
      // too, because that is what carries the pending row for the course just requested.
      queryClient.invalidateQueries({ queryKey: ['admin-tool-courses'] });
      queryClient.invalidateQueries({ queryKey: ['studio-home'] });
      // Straight into the outline: creating a course and landing back on a list is a
      // dead end, because the next thing anyone wants is to add content. Our own page,
      // not `result.url` — that is Studio's outline path, and Verawood deleted it.
      if (result.course_key) {
        navigate(`/admin/courses/${encodeURIComponent(result.course_key)}`);
      } else {
        onDone();
      }
    },
  });

  return (
    <Card title="New course">
      <form
        className="console__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) create.mutate();
        }}
      >
        <Field
          label="Course title"
          name="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          hint="What learners see. Everything else is derived from it."
          required
          autoFocus
        />

        {/* Only shown when there is a genuine choice. */}
        {orgs.length > 1 && (
          <SelectField
            label="Organisation"
            name="org"
            value={org}
            onChange={(e) => setOrg(e.target.value)}
            options={orgs.map((o) => [o, o])}
          />
        )}

        <p className="admin__derived">
          Course ID <code>{key}</code>
          <button type="button" className="ui-btn" data-variant="ghost" onClick={() => setAdvanced(!advanced)}>
            {advanced ? 'Hide' : 'Change'}
          </button>
        </p>

        {advanced && (
          <div className="admin__advanced">
            <Field
              label="Course number"
              name="number"
              value={effectiveNumber}
              onChange={(e) => setNumber(e.target.value.toUpperCase())}
              hint="Permanent. Letters and numbers only."
            />
            <Field
              label="Run"
              name="run"
              value={run}
              onChange={(e) => setRun(e.target.value)}
              hint="A term or year, e.g. 2026 or 2026T2."
            />
          </div>
        )}

        {create.isError && <CreateError error={create.error} />}

        <div className="console__form-actions">
          <Button type="submit" variant="primary" busy={create.isPending} disabled={!title.trim()}>
            Create course
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Chip icon="info">Opens the outline so you can add content</Chip>
        </div>
      </form>
    </Card>
  );
}

/**
 * Clear a failed course create or rerun.
 *
 * `dismiss_link` is a bare Studio path (`/course_notifications/{course_key}/{id}`) answering
 * DELETE, so it was never something to NAVIGATE to — as an `<a href>` it sent the SPA to a route
 * that does not exist and left the failed row in place. It is a request, so this makes one.
 *
 * The path is proxied to Studio (see `vite.config.ts`); the link the API gives us is used as-is
 * rather than rebuilt, because it already carries the action-state id that identifies which
 * failure is being cleared.
 */
/**
 * One course's price, in the admin table's Price column (spec R6.2).
 *
 * READ-ONLY, AND THROUGH THE SEAM. `coursePriceView` is the only thing that knows the
 * difference between this course being absent from a price document that loaded — free — and
 * there being no document at all, which is `unknown` and renders "Price unavailable". A cell
 * that decided that itself would be the fourth place in the app deciding what a missing price
 * means, and the one that got it wrong is the `free` derivation this branch deleted from
 * `CourseHome`.
 *
 * `PriceTag` is the same component the learner cards use, so the number an author sees in this
 * table is rendered by the same code as the number a learner sees on the card — which is what
 * stops the two disagreeing about rounding or about the word "Free" (R5.1, R5.7).
 *
 * NO EDIT CONTROL: a price is set on the course's own settings page, next to the schedule that
 * decides whether anybody can buy it. The reason is written out at the column definition above.
 */
function PriceCell({
  course,
  prices,
}: {
  course: AcademyCourse;
  prices: PricingSeam | undefined;
}) {
  return <PriceTag view={coursePriceView(prices, course.course_key)} />;
}

function DismissFailed({ item, onDone }: { item: InProcessCourse; onDone: () => void }) {
  const dismiss = useMutation({
    mutationFn: () => openedx.delete<void>(item.dismiss_link),
    onSuccess: onDone,
  });

  return (
    <Button variant="ghost" busy={dismiss.isPending} onClick={() => dismiss.mutate()}>
      {dismiss.isError ? 'Could not dismiss — retry' : 'Dismiss'}
    </Button>
  );
}
